// Thin MCO ADM area bundle — RmaApiHelper AreaRate / InsuranceOffer / SubsidyPercent.
// Fail closed; never invent rates.

import { RmaApiHelper } from "api/ApiHelper";
import { MCOService } from "components/Insurance/MCOService";

import { loadMcoActuarialAndCropLeg } from "./mcoOfficialData.js";
import { mcoPracticeCandidates, num, pick, resolveMcoPracticeName, toShareDecimal } from "./mcoShared.js";

/** Candidate ADM plan names (try in order until InsuranceOffer hits). */
export const MCO_PLAN_NAMES = {
  67: ["Margin Cov Opt - Yield Prot", "Margin Coverage Option - Yield Protection"],
  68: ["Margin Cov Opt - Rev Prot", "Margin Coverage Option - Revenue Protection", "Margin Coverage Option"],
  69: ["Margin Cov Opt - Rev Prot w Harv Price Excl", "Margin Coverage Option - Harvest Price Exclusion"],
};

function geoFilters(crop, reinsuranceYear, typeName, practiceName) {
  return {
    reinsurance_year: String(reinsuranceYear),
    state_name: crop.stateName,
    county_name: crop.countyName,
    commodity_name: crop.commodityName,
    type_name: typeName ?? crop.type_name,
    practice_name: practiceName,
  };
}

export { mcoPracticeCandidates, resolveMcoPracticeName } from "./mcoShared.js";

/**
 * MCO ADM uses "No Type Specified" (Corn/Soy/Cotton) even when the base plan's type is
 * Grain/etc, so try it first — trying the real crop type first just draws a guaranteed
 * "Type not found" failure before falling back. Real type stays as a fallback candidate
 * for any future commodity where Watts actually does use it.
 */
export function mcoTypeCandidates(typeName) {
  return [...new Set(["No Type Specified", typeName].filter(Boolean))];
}

async function loadInsuranceOffer(filters, planName) {
  const offers = await RmaApiHelper.selectObject("InsuranceOffer", filters);
  const want = (planName || "").trim();
  return (offers || []).find((o) => (pick(o, "insurance_plan_name", "Insurance_plan_name") || "").trim() === want) ?? null;
}

async function loadProjectedPrice(filters) {
  // selectPrices() always queries Revenue Protection — MCO publishes under its own plan
  // at type "No Type Specified", so RP + that type returns nothing and we silently kept
  // the county/RP seed price (Custer NE corn: $4.62 → $6.26/ac instead of $4.56 → $6.18).
  const rows = await RmaApiHelper.selectObject("Price", filters);
  const row = (rows || []).find((r) => r.projected_price != null);
  return {
    projectedPrice: row?.projected_price != null ? num(row.projected_price) : null,
  };
}

/** MCO projected price from Price ADM — saved plan name or InsuranceOffer probe. */
export async function loadMcoProjectedPriceFromAdm(crop, reinsuranceYear, { insurancePlanName } = {}) {
  const planName = String(insurancePlanName || "").trim();
  if (planName) {
    for (const type_name of mcoTypeCandidates(crop.type_name)) {
      for (const practice_name of mcoPracticeCandidates(crop.practice_name)) {
        const { projectedPrice } = await loadProjectedPrice({
          ...geoFilters(crop, reinsuranceYear, type_name, practice_name),
          insurance_plan_name: planName,
        });
        if (projectedPrice > 0) return projectedPrice;
      }
    }
    return null;
  }

  const offers = await probeMcoInsuranceOffers(crop, reinsuranceYear);
  if (!offers.length) return null;

  const preferRevProt = (a, b) => {
    const rev = "68";
    const aRev = mcoPlanCodeFromOfferName(pick(a, "insurance_plan_name", "Insurance_plan_name")) === rev;
    const bRev = mcoPlanCodeFromOfferName(pick(b, "insurance_plan_name", "Insurance_plan_name")) === rev;
    return Number(bRev) - Number(aRev);
  };

  for (const offer of [...offers].sort(preferRevProt)) {
    const type_name = pick(offer, "type_name", "Type_name") || "No Type Specified";
    const practice_name =
      pick(offer, "practice_name", "Practice_name") || resolveMcoPracticeName(crop.practice_name);
    const { projectedPrice } = await loadProjectedPrice({
      ...geoFilters(crop, reinsuranceYear, type_name, practice_name),
      insurance_plan_name: pick(offer, "insurance_plan_name", "Insurance_plan_name"),
    });
    if (projectedPrice > 0) return projectedPrice;
  }
  return null;
}

/**
 * Subsidy percent (0–1) for an area plan + coverage level (toolkit area_protection: no unit_structure_code).
 * Match coverage numerically — ADM stores ".95" while JS String(0.95) is "0.95".
 */
async function loadSubsidyPercent({ insurancePlanName, coverageLevelPercent, reinsuranceYear }) {
  const filters = {
    reinsurance_year: String(reinsuranceYear),
    coverage_type_code: "A",
    insurance_plan_name: insurancePlanName,
  };
  const rows = await RmaApiHelper.selectObject("SubsidyPercent", filters);
  const want = num(coverageLevelPercent);
  const row = (rows || []).find((r) => {
    const cov = num(pick(r, "coverage_level_percent", "Coverage_level_percent"));
    return Math.abs(cov - want) < 0.001 && num(pick(r, "subsidy_percent", "Subsidy_percent")) > 0;
  });
  return row ? num(pick(row, "subsidy_percent", "Subsidy_percent")) : null;
}

async function loadAreaRate(filters) {
  // Pull all rates for the offer geo/plan, then match coverage numerically.
  const { coverage_level_percent, ...geo } = filters;
  const rows = await RmaApiHelper.selectObject("AreaRate", geo);
  if (!rows?.length) return null;
  if (coverage_level_percent == null) return rows[0];
  const want = num(coverage_level_percent);
  return (
    rows.find((r) => Math.abs(num(pick(r, "coverage_level_percent", "Coverage_level_percent")) - want) < 0.001) ?? null
  );
}

/** All MCO InsuranceOffer rows for this county/crop/year (availability — AreaRate may lag). */
export async function loadMcoInsuranceOfferRows(crop, reinsuranceYear) {
  const mcoNames = new Set(Object.values(MCO_PLAN_NAMES).flat());
  try {
    const rows = await RmaApiHelper.selectObject("InsuranceOffer", {
      reinsurance_year: String(reinsuranceYear),
      state_name: crop.stateName,
      county_name: crop.countyName,
      commodity_name: crop.commodityName,
    });
    return (rows || []).filter((o) =>
      mcoNames.has(String(pick(o, "insurance_plan_name", "Insurance_plan_name") || "").trim())
    );
  } catch (e) {
    console.warn("loadMcoInsuranceOfferRows", e);
    return [];
  }
}

export function mcoPlanCodeFromOfferName(name) {
  const n = String(name || "").trim();
  return Object.keys(MCO_PLAN_NAMES).find((code) => MCO_PLAN_NAMES[code].includes(n)) || null;
}

/** Wide InsuranceOffer query, then per-plan probe if ADM index returns nothing. */
export async function probeMcoInsuranceOffers(crop, reinsuranceYear) {
  const wide = await loadMcoInsuranceOfferRows(crop, reinsuranceYear);
  if (wide.length) return wide;

  const found = [];
  const seen = new Set();
  for (const planNames of Object.values(MCO_PLAN_NAMES)) {
    for (const planName of planNames) {
      for (const type_name of mcoTypeCandidates(crop.type_name)) {
        for (const practice_name of mcoPracticeCandidates(crop.practice_name)) {
          const offer = await loadInsuranceOffer(
            { ...geoFilters(crop, reinsuranceYear, type_name, practice_name), insurance_plan_name: planName },
            planName
          );
          if (!offer) continue;
          const key = `${planName}|${practice_name}`;
          if (seen.has(key)) continue;
          seen.add(key);
          found.push({
            ...offer,
            insurance_plan_name: planName,
            practice_name: pick(offer, "practice_name", "Practice_name") || practice_name,
            type_name: pick(offer, "type_name", "Type_name") || type_name,
          });
        }
      }
    }
  }
  return found;
}

/** MCO coverage bands from ADM AreaRate (names only — no Official/codes). */
export async function loadMcoBandsFromAreaRate(crop, planCode, reinsuranceYear) {
  const planNames = MCO_PLAN_NAMES[planCode] || MCO_PLAN_NAMES[68];
  for (const planName of planNames) {
    for (const type_name of mcoTypeCandidates(crop.type_name)) {
      for (const practice_name of mcoPracticeCandidates(crop.practice_name)) {
        const rows = await RmaApiHelper.selectObject("AreaRate", {
          ...geoFilters(crop, reinsuranceYear, type_name, practice_name),
          insurance_plan_name: planName,
        });
        if (!rows?.length) continue;
        const bands = [];
        const seen = new Set();
        for (const r of rows) {
          let start = num(pick(r, "coverage_level_percent", "Coverage_level_percent"));
          let end = num(pick(r, "area_loss_end_percent", "Area_loss_end_percent")) || 0.86;
          if (start > 1) start /= 100;
          if (end > 1) end /= 100;
          if (!(start > end)) continue;
          const key = `${start}|${end}`;
          if (seen.has(key)) continue;
          seen.add(key);
          bands.push({ order: String(bands.length + 1), start, end });
        }
        if (bands.length) return bands.sort((a, b) => b.start - a.start);
      }
    }
  }
  return [];
}

/**
 * @param {object} crop — stateName, countyName, commodityName, type_name, practice_name, acres, yieldPerAcre, sharePct
 * @param {object} opts
 * @param {string|number} opts.planCode — 67|68|69
 * @param {number} opts.coverageStart — e.g. 0.95
 * @param {number} opts.areaLossEnd — e.g. 0.86
 * @param {number} opts.protectionFactor — PE% 0.5–1
 * @param {string|number} reinsuranceYear
 */
export async function loadMcoAreaBundle(crop, opts, reinsuranceYear) {
  const blockers = [];
  const label = crop.commodityName || "Crop";
  const planCode = String(opts.planCode || "68");
  const coverageStart = num(opts.coverageStart ?? 0.95);
  const areaLossEnd = num(opts.areaLossEnd ?? 0.86);
  const protectionFactor = num(opts.protectionFactor ?? 1);
  const coverageRange = Math.round((coverageStart - areaLossEnd) * 100) / 100;

  if (!(coverageRange > 0)) {
    return { ready: false, blockers: [`${label}: invalid coverage band`], bundle: null };
  }

  const planNames = MCO_PLAN_NAMES[planCode] || MCO_PLAN_NAMES[68];
  const typeCandidates = mcoTypeCandidates(crop.type_name);

  let offer = null;
  let planName = planNames[0];
  let typeName = typeCandidates[0];
  let resolvedPractice = resolveMcoPracticeName(crop.practice_name) || mcoPracticeCandidates(crop.practice_name)[0];
  try {
    outer: for (const name of planNames) {
      planName = name;
      for (const candidate of typeCandidates) {
        for (const practice of mcoPracticeCandidates(crop.practice_name)) {
          typeName = candidate;
          offer = await loadInsuranceOffer(
            { ...geoFilters(crop, reinsuranceYear, candidate, practice), insurance_plan_name: name },
            name
          );
          if (offer) {
            resolvedPractice = pick(offer, "practice_name", "Practice_name") || practice;
            break outer;
          }
        }
      }
    }
    if (!offer) blockers.push(`${label}: InsuranceOffer not found for plan ${planCode}`);
  } catch {
    blockers.push(`${label}: InsuranceOffer selectObject failed`);
  }

  const geo = geoFilters(crop, reinsuranceYear, typeName, resolvedPractice);

  let projectedPrice = num(crop.price);
  try {
    const prices = await loadProjectedPrice({ ...geo, insurance_plan_name: planName });
    if (prices.projectedPrice != null) projectedPrice = prices.projectedPrice;
  } catch {
    // Official PriceDiscovery may supply price; ADM price optional if crop.price set
  }

  const approvedYield = num(crop.yieldPerAcre);
  const acres = num(crop.acres);
  if (!(approvedYield > 0)) blockers.push(`${label}: approved yield missing`);
  if (!(acres > 0)) blockers.push(`${label}: acres missing`);

  let baseRate = null;
  let subsidyPercent = num(opts.officialSubsidyPercent);
  try {
    for (const practice of mcoPracticeCandidates(resolvedPractice || crop.practice_name)) {
      const rateGeo = geoFilters(crop, reinsuranceYear, typeName, practice);
      const rateRow = await loadAreaRate({
        ...rateGeo,
        insurance_plan_name: planName,
        coverage_level_percent: coverageStart,
      });
      baseRate = num(pick(rateRow, "rate", "Rate", "base_rate", "Base_rate"));
      if (baseRate > 0) break;
    }
  } catch {
    blockers.push(`${label}: AreaRate selectObject failed`);
  }

  // AreaRate often lags InsuranceOffer at early MCO SCD — Official actuarial fills the gap.
  if (!(baseRate > 0) || !(projectedPrice > 0) || !(subsidyPercent > 0)) {
    try {
      const mcoService = MCOService().actions;
      const { actuarial, cropLeg } = await loadMcoActuarialAndCropLeg(mcoService, {
        crop_year: String(reinsuranceYear),
        state_name: crop.stateName,
        county_name: crop.countyName,
        commodity_name: crop.commodityName,
        type_name: typeName,
        practice_name: resolvedPractice,
        insurance_plan_code: planCode,
        approved_yield: approvedYield,
        area_loss_order: String(opts.areaLossOrder ?? "1"),
        area_loss_start_percent: coverageStart,
        area_loss_end_percent: areaLossEnd,
        protection_factor: protectionFactor,
      });
      if (!(baseRate > 0)) {
        baseRate = num(
          pick(actuarial, "baserate", "base_rate", "rate", "premiumrate", "Baserate", "Base_rate")
        );
      }
      if (!(projectedPrice > 0)) projectedPrice = num(cropLeg?.projectedPrice);
      if (!(subsidyPercent > 0)) {
        subsidyPercent = num(pick(actuarial, "subsidyrate", "subsidy_rate", "SubsidyRate"));
      }
    } catch (e) {
      console.warn("loadMcoAreaBundle Official actuarial fallback", e);
    }
  }

  if (!(projectedPrice > 0)) blockers.push(`${label}: projected price missing`);
  if (!(baseRate > 0)) blockers.push(`${label}: AreaRate missing for plan ${planCode}`);

  if (!(subsidyPercent > 0)) {
    try {
      subsidyPercent = await loadSubsidyPercent({
        insurancePlanName: planName,
        coverageLevelPercent: coverageStart,
        reinsuranceYear,
      });
    } catch {
      blockers.push(`${label}: SubsidyPercent (plan ${planCode}) failed`);
    }
  }
  if (!(subsidyPercent > 0)) {
    blockers.push(`${label}: subsidy percent missing for plan ${planCode}`);
  }

  if (blockers.length) {
    return { ready: false, blockers, bundle: null };
  }

  return {
    ready: true,
    blockers: [],
    bundle: {
      planCode,
      planName,
      typeName, // ADM-resolved (often "No Type Specified" for Corn/Soy/Cotton)
      projectedPrice,
      approvedYield,
      acres,
      coverageStart,
      areaLossEnd,
      coverageRange,
      protectionFactor,
      insuredShareDecimal: toShareDecimal(crop.sharePct),
      baseRate,
      subsidyPercent,
      multiplicativeOptionalRateAdj: 1,
    },
  };
}
